import type { FeedbackQuestionResponse } from '@lms/api-client/backend';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { supportedLanguages } from '@/features/locale/supported-languages';
import {
  type QuestionInput,
  type QuestionText,
  questionSchema,
} from '@/features/survey/actions.schema';
import { updateQuestion } from '@/features/survey/survey-admin-api';
import { QuestionFields } from './question-fields';

function fromRecord(record: Record<string, string>): QuestionText {
  return Object.fromEntries(
    supportedLanguages.map((locale) => [locale, record[locale] ?? '']),
  ) as QuestionText;
}

// Ports features/survey/components/update-question.tsx. The Next conform form
// driven by a server action becomes a controlled form pre-filled from the
// existing question and calling `updateQuestion`; the caller navigates to the
// surveys list via `onSuccess` in place of the action's `redirect`.
export default function UpdateQuestionForm({
  question,
  onSuccess,
}: {
  question: FeedbackQuestionResponse;
  onSuccess: () => void;
}) {
  const t = useTranslations('survey');
  const [value, setValue] = useState<QuestionInput>({
    description: question.description,
    beforeQuestion: fromRecord(question.beforeQuestion),
    afterQuestion: fromRecord(question.afterQuestion),
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = questionSchema.safeParse(value);
    if (!submission.success) {
      setError(t('description'));
      return;
    }

    setPending(true);
    try {
      const result = await updateQuestion(question.id, submission.data);
      if (result.ok) {
        onSuccess();
      } else {
        setError(result.error);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <QuestionFields
        value={value}
        onChange={setValue}
        idPrefix="update-question"
      />
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton label={t('save_question')} pending={pending} />
    </form>
  );
}
